// File: src/pages/CustomerVoicePage.jsx
// Social Intelligence Dashboard - Customer Voice & Social Listening
// Uses BaoBrain signature colors: Teal (#00A8A9), Purple (#6A0DAD)
// FIXED: Corrected useData hook calls to match the hook's expected signature

import React, { useContext, useMemo, useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  Box,
  Icon,
  Badge,
  Select,
  BlockStack,
  InlineStack,
  Button,
  Spinner,
} from '@shopify/polaris';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import { 
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalIcon,
  GlobeIcon,
  SearchIcon,
  StarFilledIcon,
  AlertTriangleIcon,
  ChatIcon,
  HashtagIcon,
  PlayIcon,
  ImageIcon,
  ProductIcon,
  AlertCircleIcon,
} from '@shopify/polaris-icons';

import '../components/Layout/ui-shell.css';
import { AuthContext } from '../context/AuthContext';
import LayoutWrapper from '../components/Layout/Layout';
import useData from '../hooks/useData';
import { cleanText } from '../utils/cleanText';
import PrescriptionBanner from '../components/Prescriptions/PrescriptionBanner';

// --- PageGoal component with plain text ---
function PageGoal({ goal }) {
  return (
    <Text as="p" tone="subdued">
      <Text as="span" fontWeight="bold">Page Goal:</Text> {cleanText(goal)}
    </Text>
  );
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// REDUCED cache TTL for testing - change back to 24 hours after debugging
//const TTL_MS = 5 * 60 * 1000; // 5 minutes for testing (was 24 hours)
const TTL_MS = 24 * 60 * 60 * 1000;
// ========================================
// SIGNATURE COLORS
// ========================================
const COLORS = {
  teal: '#00A8A9',
  tealLight: 'rgba(0, 168, 169, 0.1)',
  tealMedium: 'rgba(0, 168, 169, 0.2)',
  purple: '#6A0DAD',
  purpleLight: 'rgba(106, 13, 173, 0.1)',
  purpleMedium: 'rgba(106, 13, 173, 0.2)',
  neutral: '#6b7280',
  neutralLight: 'rgba(107, 114, 128, 0.1)',
};

// ========================================
// HELPER FUNCTIONS
// ========================================

// Sentiment classification using signature colors
const getSentimentInfo = (score) => {
  if (score >= 0.25) return { level: 'positive', tone: 'success', label: 'Positive', color: COLORS.teal };
  if (score <= -0.25) return { level: 'negative', tone: 'critical', label: 'Negative', color: COLORS.purple };
  return { level: 'neutral', tone: 'info', label: 'Neutral', color: COLORS.neutral };
};

// Platform icons - using neutral styling
const getPlatformInfo = (source) => {
  const platforms = {
    'reddit': { icon: ChatIcon },
    'twitter': { icon: HashtagIcon },
    'tiktok': { icon: PlayIcon },
    'instagram': { icon: ImageIcon },
    'youtube': { icon: PlayIcon },
    'trustpilot': { icon: StarFilledIcon },
    'reviews': { icon: StarFilledIcon },
    'etsy': { icon: ProductIcon },
    'ebay': { icon: ProductIcon },
    'g2': { icon: StarFilledIcon },
  };
  const key = (source || '').toLowerCase();
  return platforms[key] || { icon: ChatIcon };
};

// Rating to stars display
const getRatingStars = (rating) => {
  const fullStars = Math.floor(rating || 0);
  const hasHalf = (rating || 0) - fullStars >= 0.5;
  return {
    full: fullStars,
    half: hasHalf,
    empty: 5 - fullStars - (hasHalf ? 1 : 0)
  };
};

// Calculate business impact from sentiment data
const calculateSentimentImpact = (summary, socialData, avgOrderValue = 75) => {
  if (socialData?.has_data) {
    const score = socialData.sentiment_score || 0;
    const sampleSize = socialData.sample_size || 0;
    const negativePct = socialData.negative_pct || 0;
    
    return { 
      reputationScore: Math.round((score + 1) * 50),
      atRiskRevenue: Math.round(sampleSize * negativePct * 0.5),
      positiveImpact: Math.round(sampleSize * (socialData.positive_pct || 0) * 0.3),
      reviewsToAddress: Math.round(sampleSize * negativePct / 100)
    };
  }
  
  if (!summary || !summary.overall) {
    return { 
      reputationScore: 0, 
      atRiskRevenue: 0, 
      positiveImpact: 0,
      reviewsToAddress: 0 
    };
  }

  const { total_reviews, avg_rating, sentiment_distribution } = summary.overall;
  const negative = sentiment_distribution?.negative || 0;
  const positive = sentiment_distribution?.positive || 0;
  
  const reputationScore = Math.round(
    ((avg_rating || 0) / 5) * 50 + 
    (positive / Math.max(total_reviews, 1)) * 50
  );
  
  const atRiskRevenue = Math.round(negative * 10 * avgOrderValue * 0.03);
  const positiveImpact = Math.round(positive * 5 * avgOrderValue * 0.02);
  
  return {
    reputationScore,
    atRiskRevenue,
    positiveImpact,
    reviewsToAddress: negative
  };
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload[0]) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '12px 16px',
        border: `1px solid ${COLORS.tealMedium}`,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 168, 169, 0.15)',
      }}>
        <p style={{ margin: 0, fontWeight: 'bold', color: '#111827' }}>{label}</p>
        <p style={{ margin: '4px 0 0', color: COLORS.teal }}>
          {payload[0].name}: {typeof payload[0].value === 'number' ? payload[0].value.toFixed(2) : payload[0].value}
        </p>
      </div>
    );
  }
  return null;
};

// ========================================
// COMPONENT: Inactive Site Banner
// ========================================
function InactiveSiteBanner({ siteStatus, onRequestActivation, onDecline }) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [declined, setDeclined] = useState(false);

  const shouldShow = siteStatus && siteStatus.is_active === false;
  
  if (!shouldShow) {
    return null;
  }

  if (declined) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <Banner
          title="No Problem!"
          tone="info"
          onDismiss={() => setDeclined(false)}
        >
          <p>
            No worries! We'll keep your site active so you can continue collecting other 
            insights like analytics and conversion data. If you ever want to enable social 
            listening later, just let us know.
          </p>
        </Banner>
      </div>
    );
  }

  if (requestSent) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <Banner
          title="We'll Start Social Listening!"
          tone="success"
          onDismiss={() => setRequestSent(false)}
        >
          <p>
            Thanks for letting us know you're still using BaoBrain! We'll begin social 
            listening for your store and you'll start seeing insights about what people 
            are saying about your products.
          </p>
        </Banner>
      </div>
    );
  }

  const handleRequestActivation = async () => {
    setIsRequesting(true);
    try {
      if (onRequestActivation) {
        await onRequestActivation();
      }
      setRequestSent(true);
    } catch (error) {
      // Silent fail - user sees no change
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDecline = () => {
    setDeclined(true);
    if (onDecline) {
      onDecline();
    }
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <Banner
        title="Social Listening Paused"
        tone="warning"
        icon={AlertCircleIcon}
      >
        <BlockStack gap="300">
          <p>
            We've marked your store as inactive due to inactivity. If you're still using 
            BaoBrain, let us know so we can reactivate social listening for your store — 
            this gives you real-time insights into what customers are saying about your 
            products across Reddit, Twitter, TikTok, and review sites.
          </p>
          <InlineStack gap="300">
            <Button
              onClick={handleRequestActivation}
              loading={isRequesting}
              variant="primary"
            >
              Yes, Reactivate Social Listening
            </Button>
            <Button
              onClick={handleDecline}
              variant="plain"
            >
              No Thanks, Keep Other Insights Only
            </Button>
          </InlineStack>
        </BlockStack>
      </Banner>
    </div>
  );
}

// ========================================
// COMPONENT: Social Mention Card
// ========================================
function SocialMentionCard({ mention }) {
  const platformInfo = getPlatformInfo(mention.source);
  const sentimentInfo = getSentimentInfo(
    mention.sentiment === 'positive' ? 0.5 : 
    mention.sentiment === 'negative' ? -0.5 : 0
  );
  
  const bgColor = mention.sentiment === 'positive' ? COLORS.tealLight : 
                  mention.sentiment === 'negative' ? COLORS.purpleLight : 
                  COLORS.neutralLight;
  const borderColor = mention.sentiment === 'positive' ? COLORS.tealMedium : 
                      mention.sentiment === 'negative' ? COLORS.purpleMedium : 
                      'rgba(107, 114, 128, 0.2)';
  
  return (
    <div style={{
      padding: '16px',
      borderRadius: '12px',
      background: bgColor,
      border: `1px solid ${borderColor}`,
      marginBottom: '12px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}>
      <InlineStack align="space-between" blockAlign="start">
        <InlineStack gap="200" blockAlign="center">
          <span style={{ color: COLORS.purple }}>
            <Icon source={platformInfo.icon} />
          </span>
          <div>
            <Text variant="bodyMd" fontWeight="semibold">
              {mention.source}
            </Text>
          </div>
          <Badge tone={sentimentInfo.tone}>{mention.sentiment}</Badge>
        </InlineStack>
        {mention.url && (
          <Button 
            plain 
            icon={ExternalIcon}
            url={mention.url}
            external
            accessibilityLabel="View source"
          />
        )}
      </InlineStack>
      
      <div style={{ marginTop: '12px' }}>
        <Text variant="bodyMd" as="p">
          "{mention.text}"
        </Text>
      </div>
    </div>
  );
}

// ========================================
// COMPONENT: Social Overview Card
// ========================================
function SocialOverviewCard({ socialData }) {
  if (!socialData?.has_data) return null;
  
  const sentimentInfo = getSentimentInfo(socialData.sentiment_score);
  
  return (
    <Card>
      <Box padding="400">
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <div>
              <Text variant="headingMd" as="h3">
                <InlineStack gap="200" blockAlign="center">
                  <span style={{ color: COLORS.teal }}>
                    <Icon source={GlobeIcon} />
                  </span>
                  <span>Social Listening Analysis</span>
                </InlineStack>
              </Text>
              <Text variant="bodySm" tone="subdued">
                Powered by Gemini AI • {socialData.sample_size} mentions analyzed
              </Text>
            </div>
            <Badge tone={sentimentInfo.tone} size="large">
              {sentimentInfo.label} ({(socialData.sentiment_score * 10).toFixed(1)}/10)
            </Badge>
          </InlineStack>
          
          {/* Sentiment Breakdown Bar */}
          <div>
            <div style={{ 
              display: 'flex', 
              height: '28px', 
              borderRadius: '14px', 
              overflow: 'hidden',
              marginBottom: '12px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ 
                width: `${socialData.positive_pct}%`, 
                background: `linear-gradient(135deg, ${COLORS.teal} 0%, #00CED1 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {socialData.positive_pct > 10 && `${socialData.positive_pct}%`}
              </div>
              <div style={{ 
                width: `${socialData.neutral_pct}%`, 
                background: COLORS.neutral,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {socialData.neutral_pct > 10 && `${socialData.neutral_pct}%`}
              </div>
              <div style={{ 
                width: `${socialData.negative_pct}%`, 
                background: `linear-gradient(135deg, ${COLORS.purple} 0%, #8B5CF6 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {socialData.negative_pct > 10 && `${socialData.negative_pct}%`}
              </div>
            </div>
            <InlineStack gap="400">
              <Text variant="bodySm">
                <span style={{ color: COLORS.teal }}>●</span> Positive {socialData.positive_pct}%
              </Text>
              <Text variant="bodySm">
                <span style={{ color: COLORS.neutral }}>●</span> Neutral {socialData.neutral_pct}%
              </Text>
              <Text variant="bodySm">
                <span style={{ color: COLORS.purple }}>●</span> Negative {socialData.negative_pct}%
              </Text>
            </InlineStack>
          </div>
          
          {/* Key Themes */}
          {socialData.key_themes?.length > 0 && (
            <div>
              <Text variant="bodyMd" fontWeight="semibold">Key Themes</Text>
              <InlineStack gap="200" wrap>
                {socialData.key_themes.map((theme, idx) => (
                  <Badge key={idx} tone="info">{theme}</Badge>
                ))}
              </InlineStack>
            </div>
          )}
        </BlockStack>
      </Box>
    </Card>
  );
}

// ========================================
// COMPONENT: Rating Stars
// ========================================
function RatingStars({ rating }) {
  const stars = getRatingStars(rating);
  return (
    <span style={{ color: '#F59E0B', display: 'flex', gap: '2px' }}>
      {[...Array(stars.full)].map((_, i) => (
        <Icon key={`full-${i}`} source={StarFilledIcon} />
      ))}
      {stars.half && <Icon source={StarFilledIcon} />}
      {[...Array(stars.empty)].map((_, i) => (
        <Icon key={`empty-${i}`} source={StarFilledIcon} />
      ))}
    </span>
  );
}

// ========================================
// COMPONENT: Sentiment Badge
// ========================================
function SentimentBadge({ score }) {
  const info = getSentimentInfo(score);
  return <Badge tone={info.tone}>{info.label}</Badge>;
}

// ========================================
// COMPONENT: Review Card
// ========================================
function ReviewCard({ review }) {
  const bgColor = review.sentiment_label === 'positive' ? COLORS.tealLight :
                  review.sentiment_label === 'negative' ? COLORS.purpleLight :
                  COLORS.neutralLight;
  const borderColor = review.sentiment_label === 'positive' ? COLORS.tealMedium :
                      review.sentiment_label === 'negative' ? COLORS.purpleMedium :
                      'rgba(107, 114, 128, 0.2)';
  
  return (
    <div style={{
      padding: '12px 16px',
      borderRadius: '8px',
      background: bgColor,
      border: `1px solid ${borderColor}`,
      marginBottom: '12px'
    }}>
      <InlineStack align="space-between" blockAlign="start">
        <BlockStack gap="100">
          <InlineStack gap="200">
            <RatingStars rating={review.rating} />
            <SentimentBadge score={review.sentiment_score} />
          </InlineStack>
          <Text variant="bodyMd" fontWeight="semibold">
            {review.product_name || 'Unknown Product'}
          </Text>
        </BlockStack>
        <Text variant="bodySm" tone="subdued">
          {review.posted_at ? new Date(review.posted_at).toLocaleDateString() : ''}
        </Text>
      </InlineStack>
      
      <div style={{ marginTop: '8px' }}>
        <Text variant="bodyMd">
          {review.review_text?.slice(0, 200) || 'No review text'}
          {review.review_text?.length > 200 && '...'}
        </Text>
      </div>
      
      <InlineStack gap="200" style={{ marginTop: '8px' }}>
        <Text variant="bodySm" tone="subdued">
          {review.reviewer_name || 'Anonymous'}
        </Text>
        <Text variant="bodySm" tone="subdued">·</Text>
        <Text variant="bodySm" tone="subdued">
          via {review.source || 'Unknown'}
        </Text>
      </InlineStack>
    </div>
  );
}

// ========================================
// MAIN COMPONENT
// ========================================
export default function SocialIntelligencePage() {
  const { token } = useContext(AuthContext);
  const [daysFilter] = useState('365');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [showAllMentions, setShowAllMentions] = useState(false);
  
  // FIXED: Use state to track social data directly fetched
  const [socialData, setSocialData] = useState(null);
  const [socialLoading, setSocialLoading] = useState(true);
  const [socialError, setSocialError] = useState(null);

  const siteId = useMemo(() => {
    const storedId = sessionStorage.getItem('site_id');
    const parsedId = Number(storedId);
    return !isNaN(parsedId) ? parsedId : null;
  }, []);

  // ========================================
  // DIRECT FETCH FOR SOCIAL DATA (BYPASSES useData caching issues)
  // ========================================
  useEffect(() => {
    if (!siteId || !token) {
      setSocialLoading(false);
      return;
    }

    const fetchSocialData = async () => {
      setSocialLoading(true);
      setSocialError(null);
      
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/social-listening/gemini/analysis?site_id=${siteId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        //console.log('[CustomerVoice] Fetched social data:', data);
        setSocialData(data);
      } catch (error) {
        //console.error('[CustomerVoice] Error fetching social data:', error);
        setSocialError(error.message);
        setSocialData(null);
      } finally {
        setSocialLoading(false);
      }
    };

    fetchSocialData();
  }, [siteId, token]);

  // ========================================
  // DATA FETCHING (other endpoints still use useData)
  // ========================================
  
  // Fetch site status
  const { data: siteStatus } = useData(
    siteId ? `site-status:${siteId}` : null,
    () => fetch(`${API_BASE_URL}/api/sites/${siteId}/status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()),
    { ttlMs: TTL_MS, deps: [siteId, token] }
  );

  // Fetch sentiment summary
  const { 
    data: summary, 
    loading: summaryLoading 
  } = useData(
    siteId ? `social-intel:summary:${siteId}` : null,
    () => fetch(`${API_BASE_URL}/api/social-listening/sentiment/summary?site_id=${siteId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()),
    { ttlMs: TTL_MS, deps: [siteId, token] }
  );

  // Fetch reviews
  const reviewsUrl = useMemo(() => {
    if (!siteId) return null;
    const params = new URLSearchParams({ site_id: siteId, days: daysFilter, limit: '50' });
    if (sentimentFilter !== 'all') params.set('sentiment', sentimentFilter);
    return `${API_BASE_URL}/api/social-listening/reviews?${params}`;
  }, [siteId, daysFilter, sentimentFilter]);

  const { 
    data: reviewsData, 
    loading: reviewsLoading 
  } = useData(
    reviewsUrl ? `social-intel:reviews:${siteId}:${sentimentFilter}:${daysFilter}` : null,
    () => fetch(reviewsUrl, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()),
    { ttlMs: TTL_MS, deps: [reviewsUrl, token] }
  );

  // Fetch sentiment trend
  const { data: trendData } = useData(
    siteId ? `social-intel:trend:${siteId}:${daysFilter}` : null,
    () => fetch(`${API_BASE_URL}/api/social-listening/sentiment/trend?site_id=${siteId}&days=${daysFilter}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()),
    { ttlMs: TTL_MS, deps: [siteId, daysFilter, token] }
  );

  // ========================================
  // HANDLERS
  // ========================================
  
  const handleRequestActivation = async () => {
    const response = await fetch(`${API_BASE_URL}/api/sites/${siteId}/request-activation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) throw new Error('Failed');
    return response.json();
  };

  const handleDecline = () => {};

  // ========================================
  // OPTIONS
  // ========================================

  const sentimentOptions = [
    { label: 'All sentiment', value: 'all' },
    { label: 'Positive only', value: 'positive' },
    { label: 'Neutral only', value: 'neutral' },
    { label: 'Negative only', value: 'negative' },
  ];

  // ========================================
  // DERIVED DATA
  // ========================================
  const impact = useMemo(() => 
    calculateSentimentImpact(summary, socialData), 
    [summary, socialData]
  );

  const chartData = useMemo(() => {
    if (!trendData || !Array.isArray(trendData)) return [];
    return trendData.map(item => ({
      date: item.date,
      sentiment: item.avg_sentiment || 0,
      rating: item.avg_rating || 0,
      count: item.review_count || 0
    }));
  }, [trendData]);

  const sentimentPieData = useMemo(() => {
    if (socialData?.has_data) {
      return [
        { name: 'Positive', value: socialData.positive_pct, color: COLORS.teal },
        { name: 'Neutral', value: socialData.neutral_pct, color: COLORS.neutral },
        { name: 'Negative', value: socialData.negative_pct, color: COLORS.purple },
      ].filter(d => d.value > 0);
    }
    
    if (!summary?.overall?.sentiment_distribution) return [];
    const dist = summary.overall.sentiment_distribution;
    return [
      { name: 'Positive', value: dist.positive || 0, color: COLORS.teal },
      { name: 'Neutral', value: dist.neutral || 0, color: COLORS.neutral },
      { name: 'Negative', value: dist.negative || 0, color: COLORS.purple },
    ].filter(d => d.value > 0);
  }, [summary, socialData]);

  // ========================================
  // LOADING STATE
  // ========================================
  if (summaryLoading && socialLoading) {
    return (
      <LayoutWrapper>
        <SkeletonPage title="Customer Voice">
          <Layout>
            <Layout.Section>
              <Card sectioned>
                <SkeletonBodyText lines={4} />
              </Card>
            </Layout.Section>
          </Layout>
        </SkeletonPage>
      </LayoutWrapper>
    );
  }

  // FIXED: Better check for data availability
  const hasAnyData = (socialData?.has_data && socialData?.sample_size > 0) || (summary?.overall?.total_reviews > 0);


  // ========================================
  // EMPTY STATE
  // ========================================
  if (!hasAnyData && !socialLoading) {
    return (
      <LayoutWrapper>
        <Page 
          title="Customer Voice"
          backAction={{
            content: 'Back',
            onAction: () => window.history.back(),
            icon: ArrowLeftIcon,
          }}
        >
          <Layout>
            <Layout.Section>
              <InactiveSiteBanner 
                siteStatus={siteStatus} 
                onRequestActivation={handleRequestActivation}
                onDecline={handleDecline}
              />
            </Layout.Section>

            <Layout.Section>
              <Card>
                <Box padding="800">
                  <BlockStack gap="400" inlineAlign="center">
                    <div style={{ 
                      color: COLORS.teal,
                      background: COLORS.tealLight,
                      padding: '16px',
                      borderRadius: '50%'
                    }}>
                      <Icon source={SearchIcon} />
                    </div>
                    <Text variant="headingLg" as="h2">No Social Data Yet</Text>
                    <Text tone="subdued" alignment="center">
                      BaoBrain uses AI to monitor what customers are saying about your brand 
                      across Reddit, Twitter, TikTok, and review sites. Run the social listening 
                      sync to start collecting data.
                    </Text>
                    {socialError && (
                      <Banner tone="warning">
                        <p>Error loading social data: {socialError}</p>
                      </Banner>
                    )}
                  </BlockStack>
                </Box>
              </Card>
            </Layout.Section>
            
            <Layout.Section>
              <Banner tone="info">
                <strong>How it works:</strong> Our AI searches social platforms for mentions of your 
                brand and products, then analyzes sentiment to help you understand customer perception.
              </Banner>
            </Layout.Section>

            <Layout.Section>
              <InlineStack gap="300" align="center">
                <Button primary onClick={() => window.location.reload()}>
                  Refresh Data
                </Button>
              </InlineStack>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }

  const chartHeight = 300;

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <LayoutWrapper>
      <Page
        title="Customer Voice"
        backAction={{
          content: 'Back',
          onAction: () => window.history.back(),
          icon: ArrowLeftIcon,
        }}
      >
        <PageGoal goal="Monitor customer sentiment across social media and review sites to protect your brand and find opportunities." />
        <div style={{
          height: 4, 
          width: '100%', 
          borderRadius: 4, 
          margin: '4px 0 12px',
          background: 'linear-gradient(90deg, var(--accent), var(--accent-2))'
        }} />

        <InactiveSiteBanner 
          siteStatus={siteStatus} 
          onRequestActivation={handleRequestActivation}
          onDecline={handleDecline}
        />

        <PrescriptionBanner 
          siteId={siteId}
          token={token}
          category="social_sentiment"
        />

        <Layout>
          {/* Social Overview */}
          {socialData?.has_data && (
            <Layout.Section>
              <SocialOverviewCard socialData={socialData} />
            </Layout.Section>
          )}
          
          {/* Business Impact Cards */}
          <Layout.Section>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <Text variant="bodySm" tone="subdued">Reputation Score</Text>
                    <Text variant="headingXl" as="p" style={{ color: COLORS.teal }}>
                      {impact.reputationScore}/100
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
              
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <Text variant="bodySm" tone="subdued">At-Risk Revenue</Text>
                    <Text variant="headingXl" as="p" style={{ color: COLORS.purple }}>
                      ${impact.atRiskRevenue.toLocaleString()}
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
              
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <Text variant="bodySm" tone="subdued">Positive Impact</Text>
                    <Text variant="headingXl" as="p" style={{ color: COLORS.teal }}>
                      ${impact.positiveImpact.toLocaleString()}
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
              
              <Card>
                <Box padding="400">
                  <BlockStack gap="200">
                    <Text variant="bodySm" tone="subdued">Reviews to Address</Text>
                    <Text variant="headingXl" as="p" style={{ color: impact.reviewsToAddress > 0 ? COLORS.purple : COLORS.neutral }}>
                      {impact.reviewsToAddress}
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            </div>
          </Layout.Section>

          {/* Charts Section */}
          <Layout.Section>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '16px' }}>
              <Card sectioned>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600' }}>Sentiment Trend</h3>
                {chartData.length > 0 ? (
                  <div style={{ height: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="sentimentGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.teal} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={COLORS.teal} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                        <XAxis dataKey="date" stroke={COLORS.neutral} fontSize={11} />
                        <YAxis domain={[-1, 1]} stroke={COLORS.neutral} fontSize={11} />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="sentiment"
                          name="Sentiment"
                          stroke={COLORS.teal}
                          fillOpacity={1}
                          fill="url(#sentimentGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div style={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text variant="bodyMd" tone="subdued" alignment="center">
                      No trend data available
                    </Text>
                  </div>
                )}
              </Card>

              <Card sectioned>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600' }}>Sentiment Distribution</h3>
                {sentimentPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <PieChart>
                      <Pie
                        data={sentimentPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={60}
                        outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {sentimentPieData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text variant="bodyMd" tone="subdued">No data available</Text>
                  </div>
                )}
              </Card>
            </div>
          </Layout.Section>

          {/* Social Mentions */}
          {socialData?.mentions?.length > 0 && (
            <Layout.Section>
              <Card sectioned>
                <InlineStack gap="200" blockAlign="center" align="space-between">
                  <InlineStack gap="200" blockAlign="center">
                    <span style={{ color: COLORS.teal }}>
                      <Icon source={GlobeIcon} />
                    </span>
                    <Text variant="headingMd" as="h3">Social Mentions</Text>
                    <Badge tone="info">{socialData.mentions.length} mentions</Badge>
                  </InlineStack>
                </InlineStack>
                <div style={{ marginTop: '16px' }}>
                  <BlockStack gap="200">
                    {(showAllMentions ? socialData.mentions : socialData.mentions.slice(0, 4)).map((mention, idx) => (
                      <SocialMentionCard key={idx} mention={mention} />
                    ))}
                    
                    {socialData.mentions.length > 4 && (
                      <Button 
                        plain 
                        onClick={() => setShowAllMentions(!showAllMentions)}
                        icon={showAllMentions ? ChevronUpIcon : ChevronDownIcon}
                      >
                        {showAllMentions ? 'Show less' : `Show ${socialData.mentions.length - 4} more`}
                      </Button>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Layout.Section>
          )}

          {/* Product Reviews */}
          {reviewsData?.reviews?.length > 0 && (
            <Layout.Section>
              <Card sectioned>
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="200" blockAlign="center">
                    <span style={{ color: COLORS.teal }}>
                      <Icon source={StarFilledIcon} />
                    </span>
                    <Text variant="headingMd" as="h3">Product Reviews</Text>
                    <Badge tone="info">{reviewsData.total} reviews</Badge>
                  </InlineStack>
                  <Select
                    label=""
                    labelHidden
                    options={sentimentOptions}
                    value={sentimentFilter}
                    onChange={setSentimentFilter}
                  />
                </InlineStack>
                <div style={{ marginTop: '16px' }}>
                  {reviewsLoading ? (
                    <Box padding="400">
                      <InlineStack gap="200" blockAlign="center">
                        <Spinner size="small" />
                        <Text>Loading reviews...</Text>
                      </InlineStack>
                    </Box>
                  ) : (
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {reviewsData.reviews.slice(0, 10).map((review) => (
                        <ReviewCard key={review.id} review={review} />
                      ))}
                      {reviewsData.total > 10 && (
                        <Text tone="subdued" alignment="center">
                          Showing 10 of {reviewsData.total} reviews
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </Layout.Section>
          )}

          {/* Products Needing Attention */}
          {summary?.attention_needed?.length > 0 && (
            <Layout.Section>
              <Card sectioned>
                <InlineStack gap="200" blockAlign="center">
                  <span style={{ color: COLORS.purple }}>
                    <Icon source={AlertTriangleIcon} />
                  </span>
                  <Text variant="headingMd" as="h3">Products Needing Attention</Text>
                  <Badge tone="critical">{summary.attention_needed.length}</Badge>
                </InlineStack>
                <div style={{ marginTop: '16px' }}>
                  <BlockStack gap="200">
                    {summary.attention_needed.map((product) => (
                      <div key={product.product_id} style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        background: COLORS.purpleLight,
                        border: `1px solid ${COLORS.purpleMedium}`
                      }}>
                        <InlineStack align="space-between">
                          <div>
                            <Text variant="bodyMd" fontWeight="semibold">{product.product_name}</Text>
                            <InlineStack gap="200">
                              <RatingStars rating={product.avg_rating} />
                              <Text variant="bodySm" tone="subdued">
                                {product.negative_count} negative of {product.total_reviews} reviews
                              </Text>
                            </InlineStack>
                          </div>
                          <Badge tone="critical">Needs attention</Badge>
                        </InlineStack>
                      </div>
                    ))}
                  </BlockStack>
                </div>
              </Card>
            </Layout.Section>
          )}

          {/* Top Products */}
          {summary?.top_products?.length > 0 && (
            <Layout.Section>
              <Card sectioned>
                <InlineStack gap="200" blockAlign="center">
                  <span style={{ color: COLORS.teal }}>
                    <Icon source={StarFilledIcon} />
                  </span>
                  <Text variant="headingMd" as="h3">Top Rated Products</Text>
                  <Badge tone="success">{summary.top_products.length}</Badge>
                </InlineStack>
                <div style={{ marginTop: '16px' }}>
                  <BlockStack gap="200">
                    {summary.top_products.map((product) => (
                      <div key={product.product_id} style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        background: COLORS.tealLight,
                        border: `1px solid ${COLORS.tealMedium}`
                      }}>
                        <InlineStack align="space-between">
                          <div>
                            <Text variant="bodyMd" fontWeight="semibold">{product.product_name}</Text>
                            <InlineStack gap="200">
                              <RatingStars rating={product.avg_rating} />
                              <Text variant="bodySm" tone="subdued">
                                {product.positive_count} positive of {product.total_reviews} reviews
                              </Text>
                            </InlineStack>
                          </div>
                          <Badge tone="success">Safe to promote</Badge>
                        </InlineStack>
                      </div>
                    ))}
                  </BlockStack>
                </div>
              </Card>
            </Layout.Section>
          )}

        </Layout>
      </Page>
    </LayoutWrapper>
  );
}